/**
 * <sfk-popup> — the newsletter / promo-code modal.
 *
 * Remembers a dismissal in localStorage for the number of days configured on
 * the section, and never opens inside the theme editor unless the merchant has
 * that section selected (so it doesn't fight with editing the rest of the page).
 */
class SfkPopup extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('[data-sfk-popup-panel]');
    if (!this.panel) return;

    this.storageKey = `sfk-popup:${this.dataset.sectionId || 'default'}`;
    this.days = parseInt(this.dataset.days, 10) || 0;
    this.delay = (parseFloat(this.dataset.delay) || 0) * 1000;

    this.querySelectorAll('[data-sfk-popup-close]').forEach((el) => {
      el.addEventListener('click', () => this.close(true));
    });

    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close(true);
    });

    // A successful signup comes back as ?customer_posted=true — show the
    // confirmation rather than the form.
    const params = new URLSearchParams(window.location.search);
    if (params.get('customer_posted') === 'true' && this.querySelector('[data-sfk-popup-success]')) {
      this.open();
      return;
    }

    if (this.dismissed()) return;

    window.setTimeout(() => this.open(), this.delay);
  }

  dismissed() {
    if (this.dataset.designMode === 'true') return false;
    try {
      const until = window.localStorage.getItem(this.storageKey);
      return until !== null && Date.now() < parseInt(until, 10);
    } catch (e) {
      // Private browsing or blocked storage — just show the popup.
      return false;
    }
  }

  open() {
    if (!this.isConnected) return;
    this.classList.add('is-open');
    this.lastFocused = document.activeElement;
    const focusable = this.panel.querySelector('input, button, [href]');
    if (focusable) focusable.focus({ preventScroll: true });
  }

  close(remember) {
    this.classList.remove('is-open');
    if (remember && this.days > 0) {
      try {
        window.localStorage.setItem(this.storageKey, String(Date.now() + this.days * 86400000));
      } catch (e) {
        /* storage unavailable — nothing to remember */
      }
    }
    if (this.lastFocused instanceof HTMLElement) this.lastFocused.focus({ preventScroll: true });
  }
}

if (!customElements.get('sfk-popup')) {
  customElements.define('sfk-popup', SfkPopup);
}
